import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Settings as SettingsIcon, BookOpen, Star, Inbox, Plus, Trash2, Save, Eye, EyeOff } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GRADE_OPTIONS } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  getSiteSettings, updateSiteSettings,
  listAllServices, createService, updateService, deleteService,
  listAllTestimonials, createTestimonial, updateTestimonial, deleteTestimonial,
  listWaitlist, updateWaitlistStatus, deleteWaitlist,
  fetchPublicStrict,
} from "@/lib/data";
import { formatSupabaseError } from "@/lib/supabase";

const fmtErr = formatSupabaseError;

// After an edit, re-read the public data to confirm guests will see the change.
async function confirmLive(key, isLive) {
  try {
    const data = await fetchPublicStrict(key);
    if (!isLive(data)) throw new Error("The public site is still returning the previous version.");
  } catch (e) {
    toast.error("Saved — but the public site may not be showing this change yet", {
      description: `${fmtErr(e)} Give it a minute and refresh the site; if it persists, check Supabase.`,
      duration: 20000,
    });
  }
}

const sameRow = (rows, saved, fields) =>
  rows.some((r) => r.id === saved.id && fields.every((f) => JSON.stringify(r[f]) === JSON.stringify(saved[f])));
const absent = (id) => (rows) => !rows.some((r) => r.id === id);

// ---------------- Settings Tab ----------------
function SettingsPanel() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSiteSettings().then((d) => setForm(d || {})).catch((e) => toast.error(fmtErr(e)));
  }, []);

  if (!form) return <div className="text-ink/60" data-testid="settings-loading">Loading...</div>;

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const data = await updateSiteSettings(form);
      setForm(data);
      toast.success("Settings updated");
      confirmLive("settings", (live) => !!live && ["business_email", "business_phone", "service_area"].every((f) => live[f] === data[f]));
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="rounded-2xl border border-ink/5" data-testid="settings-panel">
      <CardContent className="p-6 sm:p-8 space-y-5">
        <div>
          <h2 className="font-display text-xl font-semibold text-ink">Site Settings</h2>
          <p className="text-sm text-ink/60 mt-1">These appear in the footer, contact page, and across the public site.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <Label>Business email</Label>
            <Input value={form.business_email || ""} onChange={onChange("business_email")} placeholder="jonna@dyslexiatutor.com" className="rounded-xl h-11" data-testid="settings-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Business phone</Label>
            <Input value={form.business_phone || ""} onChange={onChange("business_phone")} placeholder="(937) 555-1234" className="rounded-xl h-11" data-testid="settings-phone" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Service area</Label>
          <Input value={form.service_area || ""} onChange={onChange("service_area")} className="rounded-xl h-11" data-testid="settings-area" />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <Label>LinkedIn URL</Label>
            <Input value={form.linkedin_url || ""} onChange={onChange("linkedin_url")} className="rounded-xl h-11" data-testid="settings-linkedin" />
          </div>
          <div className="space-y-1.5">
            <Label>Facebook URL</Label>
            <Input value={form.facebook_url || ""} onChange={onChange("facebook_url")} className="rounded-xl h-11" data-testid="settings-facebook" />
          </div>
          <div className="space-y-1.5">
            <Label>Instagram URL</Label>
            <Input value={form.instagram_url || ""} onChange={onChange("instagram_url")} className="rounded-xl h-11" data-testid="settings-instagram" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Booking note (shown on contact page)</Label>
          <Textarea rows={3} value={form.booking_note || ""} onChange={onChange("booking_note")} className="rounded-xl" data-testid="settings-note" />
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-ink/5">
          <div className="flex items-center gap-3">
            <Switch checked={!!form.waitlist_open} onCheckedChange={(v) => setForm((f) => ({ ...f, waitlist_open: v }))} data-testid="settings-waitlist-toggle" />
            <span className="text-sm text-ink">Waitlist is open to new submissions</span>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-11 px-6" data-testid="settings-save">
            <Save size={16} className="mr-1.5" />{saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------- Services Tab ----------------
const BLANK_SERVICE = { title: "", slug: "", price_label: "", duration: "60 minutes", description: "", features: [], recommended_for: "", is_active: true, sort_order: 0 };

function ServicesPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // service object or "new"

  const load = () => listAllServices().then(setItems).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing, features: typeof editing.features === "string" ? editing.features.split("\n").map((x) => x.trim()).filter(Boolean) : editing.features };
      let saved;
      if (editing.id) {
        saved = await updateService(editing.id, payload);
        toast.success("Service updated");
      } else {
        const { id: _omit, ...rest } = payload;
        saved = await createService(rest);
        toast.success("Service created");
      }
      setEditing(null); load();
      confirmLive("services", (rows) => saved.is_active
        ? sameRow(rows, saved, ["title", "price_label", "description", "features"])
        : absent(saved.id)(rows));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await deleteService(id); toast.success("Deleted"); load(); confirmLive("services", absent(id)); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  if (editing) {
    const onF = (k) => (e) => setEditing((s) => ({ ...s, [k]: e.target.value }));
    return (
      <Card className="rounded-2xl border border-ink/5" data-testid="service-editor">
        <CardContent className="p-6 sm:p-8 space-y-5">
          <h2 className="font-display text-xl font-semibold text-ink">{editing.id ? "Edit service" : "Add a service"}</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={editing.title} onChange={onF("title")} className="rounded-xl h-11" data-testid="service-title" />
            </div>
            <div className="space-y-1.5">
              <Label>Slug</Label>
              <Input value={editing.slug} onChange={onF("slug")} placeholder="kebab-case-id" className="rounded-xl h-11" data-testid="service-slug" />
            </div>
            <div className="space-y-1.5">
              <Label>Price label</Label>
              <Input value={editing.price_label} onChange={onF("price_label")} placeholder="$65 / hour" className="rounded-xl h-11" data-testid="service-price" />
            </div>
            <div className="space-y-1.5">
              <Label>Duration</Label>
              <Input value={editing.duration} onChange={onF("duration")} className="rounded-xl h-11" data-testid="service-duration" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea rows={3} value={editing.description} onChange={onF("description")} className="rounded-xl" data-testid="service-description" />
          </div>
          <div className="space-y-1.5">
            <Label>Features (one per line)</Label>
            <Textarea rows={5} value={Array.isArray(editing.features) ? editing.features.join("\n") : editing.features} onChange={(e) => setEditing((s) => ({ ...s, features: e.target.value }))} className="rounded-xl" data-testid="service-features" />
          </div>
          <div className="space-y-1.5">
            <Label>Recommended for</Label>
            <Input value={editing.recommended_for} onChange={onF("recommended_for")} className="rounded-xl h-11" data-testid="service-recommended" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={!!editing.is_active} onCheckedChange={(v) => setEditing((s) => ({ ...s, is_active: v }))} data-testid="service-active" />
            <span className="text-sm">Active (shown on public site)</span>
            <div className="ml-auto flex items-center gap-2">
              <Label className="text-xs">Sort order</Label>
              <Input type="number" value={editing.sort_order ?? 0} onChange={(e) => setEditing((s) => ({ ...s, sort_order: parseInt(e.target.value || 0, 10) }))} className="rounded-xl h-9 w-20" data-testid="service-sort" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={save} className="rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-11 px-6" data-testid="service-save"><Save size={16} className="mr-1.5" /> Save</Button>
            <Button onClick={() => setEditing(null)} variant="outline" className="rounded-full border-2 h-11 px-6" data-testid="service-cancel">Cancel</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="services-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold text-ink">Services</h2>
          <p className="text-sm text-ink/60">Edit pricing, descriptions, and visibility.</p>
        </div>
        <Button onClick={() => setEditing({ ...BLANK_SERVICE })} className="rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-11 px-5" data-testid="service-add">
          <Plus size={16} className="mr-1.5" /> Add service
        </Button>
      </div>
      <div className="grid gap-4">
        {items.map((s) => (
          <Card key={s.id} className="rounded-2xl border border-ink/5" data-testid={`service-row-${s.slug}`}>
            <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row gap-4 justify-between sm:items-center">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-lg font-semibold text-ink">{s.title}</h3>
                  <Badge variant="secondary" className="rounded-full">{s.price_label}</Badge>
                  {!s.is_active && <Badge variant="outline" className="rounded-full text-ink/60">Hidden</Badge>}
                </div>
                <p className="text-sm text-ink/65 mt-1 line-clamp-2 max-w-2xl">{s.description}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setEditing({ ...s })} variant="outline" className="rounded-full h-9 border-ink/20" data-testid={`service-edit-${s.slug}`}>Edit</Button>
                <Button onClick={() => remove(s.id)} variant="outline" className="rounded-full h-9 border-destructive/30 text-destructive hover:bg-destructive hover:text-white" data-testid={`service-delete-${s.slug}`}><Trash2 size={14} /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------- Testimonials Tab ----------------
const NO_GRADE = "__none__";

function GradeSelect({ value, onChange }) {
  const isLegacy = value && !GRADE_OPTIONS.some((g) => g.value === value);
  return (
    <Select value={value || NO_GRADE} onValueChange={(v) => onChange(v === NO_GRADE ? "" : v)}>
      <SelectTrigger className="rounded-xl h-11" data-testid="testimonial-grade">
        <SelectValue placeholder="Select grade" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_GRADE} data-testid="testimonial-grade-none">Not specified</SelectItem>
        {isLegacy && <SelectItem value={value}>{value}</SelectItem>}
        {GRADE_OPTIONS.map((g) => (
          <SelectItem key={g.value} value={g.value} data-testid={`testimonial-grade-${g.value.replace(/\s+/g, "-").toLowerCase()}`}>{g.value}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
const BLANK_T = { parent_name: "", child_first_name: "", child_age_or_grade: "", quote: "", rating: 5, is_published: false, sort_order: 0 };

function TestimonialsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => listAllTestimonials().then(setItems).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const checkTestimonial = (saved) => confirmLive("testimonials", (rows) => saved.is_published
    ? sameRow(rows, saved, ["quote", "parent_name"])
    : absent(saved.id)(rows));

  const save = async () => {
    try {
      let saved;
      if (editing.id) { saved = await updateTestimonial(editing.id, editing); toast.success("Updated"); }
      else {
        const { id: _omit, ...rest } = editing;
        saved = await createTestimonial(rest); toast.success("Created");
      }
      setEditing(null); load();
      checkTestimonial(saved);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await deleteTestimonial(id); toast.success("Deleted"); load(); confirmLive("testimonials", absent(id)); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const togglePublish = async (t) => {
    try { const saved = await updateTestimonial(t.id, { is_published: !t.is_published }); load(); checkTestimonial(saved); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  if (editing) {
    const onF = (k) => (e) => setEditing((s) => ({ ...s, [k]: e.target.value }));
    return (
      <Card className="rounded-2xl border border-ink/5" data-testid="testimonial-editor">
        <CardContent className="p-6 sm:p-8 space-y-5">
          <h2 className="font-display text-xl font-semibold text-ink">{editing.id ? "Edit testimonial" : "Add a testimonial"}</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            <div className="space-y-1.5">
              <Label>Parent name</Label>
              <Input value={editing.parent_name} onChange={onF("parent_name")} className="rounded-xl h-11" data-testid="testimonial-parent" />
            </div>
            <div className="space-y-1.5">
              <Label>Child first name</Label>
              <Input value={editing.child_first_name || ""} onChange={onF("child_first_name")} className="rounded-xl h-11" data-testid="testimonial-child" />
            </div>
            <div className="space-y-1.5">
              <Label>Grade</Label>
              <GradeSelect value={editing.child_age_or_grade || ""} onChange={(v) => setEditing((s) => ({ ...s, child_age_or_grade: v }))} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Quote</Label>
            <Textarea rows={5} value={editing.quote} onChange={onF("quote")} className="rounded-xl" data-testid="testimonial-quote" />
          </div>
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-2">
              <Label className="text-xs">Rating</Label>
              <Input type="number" min={1} max={5} value={editing.rating} onChange={(e) => setEditing((s) => ({ ...s, rating: parseInt(e.target.value || 5, 10) }))} className="rounded-xl h-9 w-20" data-testid="testimonial-rating" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Sort order</Label>
              <Input type="number" value={editing.sort_order ?? 0} onChange={(e) => setEditing((s) => ({ ...s, sort_order: parseInt(e.target.value || 0, 10) }))} className="rounded-xl h-9 w-20" data-testid="testimonial-sort" />
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Switch checked={!!editing.is_published} onCheckedChange={(v) => setEditing((s) => ({ ...s, is_published: v }))} data-testid="testimonial-publish" />
              <span className="text-sm">Published</span>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={save} className="rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-11 px-6" data-testid="testimonial-save"><Save size={16} className="mr-1.5" /> Save</Button>
            <Button onClick={() => setEditing(null)} variant="outline" className="rounded-full border-2 h-11 px-6" data-testid="testimonial-cancel">Cancel</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="testimonials-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold text-ink">Reviews & Stories</h2>
          <p className="text-sm text-ink/60">Add parent testimonials. Toggle "Published" to show them on the site.</p>
        </div>
        <Button onClick={() => setEditing({ ...BLANK_T })} className="rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-11 px-5" data-testid="testimonial-add">
          <Plus size={16} className="mr-1.5" /> Add story
        </Button>
      </div>
      <div className="grid gap-4">
        {items.length === 0 && <p className="text-ink/60 text-sm">No testimonials yet.</p>}
        {items.map((t) => (
          <Card key={t.id} className="rounded-2xl border border-ink/5" data-testid={`testimonial-row-${t.id}`}>
            <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row gap-4 justify-between sm:items-center">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-ink">{t.parent_name}</span>
                  {t.child_first_name && <span className="text-xs text-ink/55">· {t.child_first_name}{t.child_age_or_grade ? `, ${t.child_age_or_grade}` : ""}</span>}
                  {t.is_published ? <Badge className="rounded-full bg-sage text-white">Published</Badge> : <Badge variant="outline" className="rounded-full">Draft</Badge>}
                </div>
                <p className="text-sm text-ink/75 mt-1.5 line-clamp-2 max-w-2xl">&ldquo;{t.quote}&rdquo;</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => togglePublish(t)} variant="outline" className="rounded-full h-9 border-ink/20" data-testid={`testimonial-toggle-${t.id}`}>
                  {t.is_published ? <><EyeOff size={14} className="mr-1" /> Unpublish</> : <><Eye size={14} className="mr-1" /> Publish</>}
                </Button>
                <Button onClick={() => setEditing({ ...t })} variant="outline" className="rounded-full h-9 border-ink/20" data-testid={`testimonial-edit-${t.id}`}>Edit</Button>
                <Button onClick={() => remove(t.id)} variant="outline" className="rounded-full h-9 border-destructive/30 text-destructive hover:bg-destructive hover:text-white" data-testid={`testimonial-delete-${t.id}`}><Trash2 size={14} /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------- Waitlist Tab ----------------
const STATUS_OPTS = ["new", "contacted", "scheduled", "archived"];

function WaitlistPanel() {
  const [items, setItems] = useState([]);

  const load = () => listWaitlist().then(setItems).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await updateWaitlistStatus(id, status); toast.success("Updated"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try { await deleteWaitlist(id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="waitlist-panel">
      <div>
        <h2 className="font-display text-xl font-semibold text-ink">Waitlist Submissions</h2>
        <p className="text-sm text-ink/60">Newest first. Update status as you reach out.</p>
      </div>
      {items.length === 0 ? (
        <p className="text-ink/60 text-sm">No submissions yet.</p>
      ) : (
        <div className="grid gap-4">
          {items.map((w) => (
            <Card key={w.id} className="rounded-2xl border border-ink/5" data-testid={`waitlist-row-${w.id}`}>
              <CardContent className="p-5 sm:p-6">
                <div className="flex flex-col lg:flex-row gap-4 lg:items-start lg:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-lg font-semibold text-ink">{w.parent_name}</h3>
                      <Badge className={`rounded-full ${w.status === "new" ? "bg-terracotta text-white" : w.status === "contacted" ? "bg-mustard text-ink" : w.status === "scheduled" ? "bg-sage text-white" : "bg-ink/10 text-ink"}`}>{w.status}</Badge>
                    </div>
                    <div className="text-sm text-ink/70 mt-1.5 flex gap-3 flex-wrap">
                      <a href={`mailto:${w.email}`} className="link-underline">{w.email}</a>
                      {w.phone && <span>· {w.phone}</span>}
                      {w.child_age && <span>· Age {w.child_age}</span>}
                      {w.child_grade && <span>· {w.child_grade}</span>}
                    </div>
                    {(w.service_interest || w.frequency_interest) && (
                      <div className="text-xs text-ink/55 mt-1">
                        {w.service_interest && <>Service: <span className="text-ink/75">{w.service_interest}</span></>}
                        {w.frequency_interest && <> · Freq: <span className="text-ink/75">{w.frequency_interest}</span></>}
                      </div>
                    )}
                    {w.concerns && <p className="mt-3 text-sm text-ink/80"><span className="text-ink/55">Concerns: </span>{w.concerns}</p>}
                    {w.message && <p className="mt-1 text-sm text-ink/80"><span className="text-ink/55">Message: </span>{w.message}</p>}
                    {w.referral_source && <p className="mt-1 text-xs text-ink/55">Heard via: {w.referral_source}</p>}
                    <p className="mt-2 text-xs text-ink/45">Received {new Date(w.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {STATUS_OPTS.map((opt) => (
                      <Button key={opt} variant={w.status === opt ? "default" : "outline"} onClick={() => setStatus(w.id, opt)} className={`rounded-full h-9 px-3 text-xs ${w.status === opt ? "bg-ink text-white" : ""}`} data-testid={`waitlist-status-${opt}-${w.id}`}>
                        {opt}
                      </Button>
                    ))}
                    <Button onClick={() => remove(w.id)} variant="outline" className="rounded-full h-9 border-destructive/30 text-destructive hover:bg-destructive hover:text-white" data-testid={`waitlist-delete-${w.id}`}><Trash2 size={14} /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Dashboard shell ----------------
export default function AdminDashboard() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => { await signOut(); navigate("/admin/login"); };

  const adminName = profile?.full_name && profile.full_name.trim() ? String(profile.full_name).split(" ")[0] : "";

  return (
    <main className="min-h-[100svh] bg-cream" data-testid="admin-dashboard">
      <header className="bg-white border-b border-ink/5 sticky top-0 z-30">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/admin"><Logo size={32} /></Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-ink/60 hover:text-ink link-underline" data-testid="admin-view-site">View site →</Link>
            <span className="hidden sm:inline-flex text-sm text-ink/60">{user?.email}</span>
            <Button onClick={handleLogout} variant="outline" className="rounded-full border-ink/20 h-9 px-4" data-testid="admin-logout">
              <LogOut size={14} className="mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-10">
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-ink">Welcome back{adminName ? `, ${adminName}` : ""}.</h1>
          <p className="text-ink/60 mt-1">Manage your services, stories, waitlist, and contact info.</p>
        </div>
        <Tabs defaultValue="waitlist" className="w-full">
          <TabsList className="bg-white border border-ink/5 rounded-full p-1 h-auto flex flex-wrap" data-testid="admin-tabs">
            <TabsTrigger value="waitlist" className="rounded-full px-4 py-2 data-[state=active]:bg-ink data-[state=active]:text-white" data-testid="tab-waitlist"><Inbox size={14} className="mr-1.5" /> Waitlist</TabsTrigger>
            <TabsTrigger value="testimonials" className="rounded-full px-4 py-2 data-[state=active]:bg-ink data-[state=active]:text-white" data-testid="tab-testimonials"><Star size={14} className="mr-1.5" /> Stories</TabsTrigger>
            <TabsTrigger value="services" className="rounded-full px-4 py-2 data-[state=active]:bg-ink data-[state=active]:text-white" data-testid="tab-services"><BookOpen size={14} className="mr-1.5" /> Services</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-full px-4 py-2 data-[state=active]:bg-ink data-[state=active]:text-white" data-testid="tab-settings"><SettingsIcon size={14} className="mr-1.5" /> Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="waitlist" className="mt-8"><WaitlistPanel /></TabsContent>
          <TabsContent value="testimonials" className="mt-8"><TestimonialsPanel /></TabsContent>
          <TabsContent value="services" className="mt-8"><ServicesPanel /></TabsContent>
          <TabsContent value="settings" className="mt-8"><SettingsPanel /></TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
